import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./SetWarrantyDetails.css";
import "../../../common.css";
import Modal from "react-modal";
import config from "@config";
// import logo from "@img/subemo.webp";
import LoadingPage from "@components/Loading/Loading";
import errorFunctions from "@store/errorFunctions";
import utilityFunctions from "@store/utilityFunctions";
import apiEndpoints from "@store/apiEndpoints";
import dateOfReturnFns from "@store/dateOfReturn";
import { useToast } from "@components/Toast/ToastContext";
import groupTicketReasonsByCategory from "./groupTicketReasonsByCategory";
import TicketReasonsCheckboxes from "./components/TicketReasonsCheckboxes";
import DateOfReturnField from "./components/DateOfReturnField";
import TicketTypeSelect from "./components/TicketTypeSelect";
import IssueDetailsFields from "./components/IssueDetailsFields";
import AttachmentsUploader from "./components/AttachmentsUploader";
import ProofOfReturnField from "./components/ProofOfReturnField";
import AddressConfirmModal from "./components/AddressConfirmModal";
Modal.setAppElement("body");

const EMPTY_ADDRESS = {
  address_line_one: "",
  address_line_two: "",
  suburb: "",
  state: "",
  postcode: "",
  country: config.country,
};

const SetWarrantyDetails = (props) => {
  const { showToast } = useToast();

  const {
    showModal,
    hideModal,
    selectedBrandIdForWarranty,
    protectedFetch,
    selectedBrand,
    selectedbrandID,
    userDevice,
    customerId,
    handleSuccess,
    handleRefresh,
    retrieveCustomerDevices,
    isRegisterDevice,
    retrieveWarranty,
    handleCloseModal,
  } = props;

  // --- State ---
  const [reason, setReason] = useState([]);
  const [ticketType, setTicketType] = useState("");
  const [dateOfReturn, setDateOfReturn] = useState(null);
  const [dorAttachments, setDorAttachments] = useState([]);
  const [typeOfService, setTypeOfService] = useState("");
  const [systemError, setSystemError] = useState("");
  const [otherIssue, setOtherIssue] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [problemFiles, setProblemFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitProgress, setSubmitProgress] = useState(false);
  // Per-field validation messages from the API, keyed by field name.
  const [fieldErrors, setFieldErrors] = useState({});

  // Delivery address confirmation (non-commercial roles only).
  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const [addressMode, setAddressMode] = useState("confirm");
  const [ticketAddress, setTicketAddress] = useState(EMPTY_ADDRESS);
  const [addressLoading, setAddressLoading] = useState(false);

  const [ticketReasonData, setTicketReasonData] = useState({
    reasons: [],
    grouped: {},
    hasCategories: false,
  });
  const [ticketReasonsLoading, setTicketReasonsLoading] = useState(true);

  const userInfo = useMemo(() => utilityFunctions.getUserInfo(), []);

  const isCommercialRole = useMemo(
    () => dateOfReturnFns.isCommercialUser(userInfo),
    [userInfo],
  );

  // --- Effects ---
  useEffect(() => {
    if (showModal) {
      setDateOfReturn(null);
      setDorAttachments([]);
      setFieldErrors({});
      setShowAddressConfirm(false);
      setAddressMode("confirm");
      fetchTicketReasons();

      if (!isCommercialRole && customerId) {
        setAddressLoading(true);
        protectedFetch(
          apiEndpoints.customer.detail(customerId),
          "GET",
          null,
          errorFunctions.checkResponseThenJson,
        )
          .then((res) => {
            setTicketAddress({
              address_line_one: res?.address_line_one || "",
              address_line_two: res?.address_line_two || "",
              suburb: res?.suburb || "",
              state: res?.state || "",
              postcode: res?.postcode || "",
              country: res?.country || config.country,
            });
          })
          .catch((err) => {
            console.log("Error fetching customer address:", err);
          })
          .finally(() => {
            setAddressLoading(false);
          });
      }
    }
  }, [showModal]);

  // --- Handlers ---
  const fetchTicketReasons = useCallback(() => {
    if (!selectedBrandIdForWarranty) {
      setTicketReasonsLoading(false);
      return;
    }

    setTicketReasonsLoading(true);
    protectedFetch(
      apiEndpoints.ticket.ticketReasonBrands(selectedBrandIdForWarranty),
      "GET",
      null,
      errorFunctions.checkResponseThenJson,
    )
      .then((res) => {
        const data = res?.data || res;
        if (Array.isArray(data) && data.length > 0) {
          const result = groupTicketReasonsByCategory(data);
          setTicketReasonData({
            reasons: result.reasons,
            grouped: result.grouped,
            hasCategories: result.hasCategories,
          });
        } else {
          setTicketReasonData({
            reasons: [],
            grouped: {},
            hasCategories: false,
          });
        }
      })
      .catch((err) => {
        console.log("Error fetching ticket reasons:", err);
      })
      .finally(() => {
        setTicketReasonsLoading(false);
      });
  }, [selectedBrandIdForWarranty, protectedFetch]);

  const handleCheckboxChange = useCallback((e) => {
    const { value, checked } = e.target;
    setReason((prev) => {
      const idx = prev.indexOf(value);
      if (checked && idx === -1) return [...prev, value];
      if (!checked && idx > -1) return prev.filter((_, i) => i !== idx);
      return prev;
    });
  }, []);

  const handleOtherIssueReason = useCallback((e) => {
    const { value } = e.target;
    setReasonOther(value);

    setReason((prev) => {
      const otherIdx = prev.findIndex((r) => r.startsWith("Other: "));
      if (value.trim()) {
        const newOther = `Other: ${value}`;
        if (otherIdx >= 0) {
          const updated = [...prev];
          updated[otherIdx] = newOther;
          return updated;
        }
        return [...prev, newOther];
      }
      if (otherIdx >= 0) {
        return prev.filter((_, i) => i !== otherIdx);
      }
      return prev;
    });
  }, []);

  const handleInputChange = useCallback((e) => {
    const { id, value } = e.target;
    switch (id) {
      case "ticketType":
        setTicketType(value);
        break;
      case "type_of_service":
        setTypeOfService(value);
        break;
      case "systemError":
        setSystemError(value);
        break;
      case "otherIssue":
        setOtherIssue(value);
        break;
      default:
        break;
    }
  }, []);

  const handleFileUpload = useCallback(
    (e) => {
      const newFiles = Array.prototype.slice.call(e.target.files);
      setProblemFiles((prev) => {
        const allFiles = [...prev, ...newFiles];
        if (allFiles.length > 5) {
          showToast("Maximum file to be uploaded is only 5", "warning");
          return allFiles.slice(0, 5);
        }
        return allFiles;
      });
    },
    [showToast],
  );

  const handleRemoveFile = useCallback((fileName) => {
    setProblemFiles((prev) => prev.filter((f) => f.name !== fileName));
  }, []);

  const handleDorAttachmentChange = useCallback(
    (e) => {
      const newFiles = Array.prototype.slice.call(e.target.files);
      // Clear the input so picking the same file(s) again still fires onChange.
      e.target.value = "";
      if (newFiles.length === 0) return;

      const invalidType = newFiles.some(
        (file) => !dateOfReturnFns.DOR_ATTACHMENT_TYPES.includes(file.type),
      );
      if (invalidType) {
        showToast("Only JPG, PNG and PDF files are allowed", "warning");
        return;
      }

      const oversized = newFiles.some(
        (file) => file.size > dateOfReturnFns.DOR_ATTACHMENT_MAX_SIZE_BYTES,
      );
      if (oversized) {
        showToast(
          `Each file must be ${dateOfReturnFns.DOR_ATTACHMENT_MAX_SIZE_MB}MB or smaller`,
          "warning",
        );
        return;
      }

      setDorAttachments((prev) => {
        const allFiles = [...prev, ...newFiles];
        if (allFiles.length > dateOfReturnFns.DOR_ATTACHMENT_MAX_FILES) {
          showToast(
            `Maximum file to be uploaded is only ${dateOfReturnFns.DOR_ATTACHMENT_MAX_FILES}`,
            "warning",
          );
          return allFiles.slice(0, dateOfReturnFns.DOR_ATTACHMENT_MAX_FILES);
        }
        return allFiles;
      });
      setFieldErrors((prev) => ({
        ...prev,
        [dateOfReturnFns.DOR_ATTACHMENT_FIELD]: undefined,
      }));
    },
    [showToast],
  );

  const handleRemoveDorAttachment = useCallback((fileName) => {
    setDorAttachments((prev) => prev.filter((f) => f.name !== fileName));
  }, []);

  const handleChangeTicketLocation = useCallback(
    (ticketId) => {
      setLoading(true);

      const payload = {
        store_location: userInfo?.user?.store_location_id,
      };

      protectedFetch(
        config.backendServer + `ticket/${ticketId}/change-location/`,
        "PATCH",
        payload,
        errorFunctions.checkResponseThenJson,
      )
        .then(() => {
          showToast("Ticket created successfully", "success");
          retrieveWarranty?.();
          handleCloseModal?.();
          setLoading(false);
        })
        .catch((err) => {
          console.log("fail", err);
          setLoading(false);
        });
    },
    [userInfo, protectedFetch, showToast, retrieveWarranty, handleCloseModal],
  );

  const submitTicket = useCallback(() => {
    setLoading(true);

    const formData = new FormData();
    formData.append("reason", reason);
    formData.append("ticket_type", ticketType || "warranty");
    formData.append("service", typeOfService);
    formData.append("system_error", systemError);
    formData.append("user_device", parseInt(userDevice) || 0);
    formData.append("other_issue", otherIssue);
    formData.append("brand_id", selectedBrandIdForWarranty);
    if (problemFiles?.length > 0) {
      problemFiles.forEach((file) => formData.append("media", file));
    }
    formData.append("customer", customerId);
    dateOfReturnFns.appendDateOfReturn(formData, dateOfReturn);
    dateOfReturnFns.appendDorAttachment(formData, dorAttachments);

    if (!isCommercialRole) {
      formData.append("address_line_one", ticketAddress.address_line_one || "");
      formData.append("address_line_two", ticketAddress.address_line_two || "");
      formData.append("suburb", ticketAddress.suburb || "");
      formData.append("state", ticketAddress.state || "");
      formData.append("postcode", ticketAddress.postcode || "");
    }

    protectedFetch(
      apiEndpoints.ticket.create(),
      "POST",
      formData,
      errorFunctions.checkResponseThenJson,
      true,
    )
      .then((res) => {
        setLoading(false);
        setShowAddressConfirm(false);

        if (!isCommercialRole) {
          showToast("Ticket created successfully", "success");
        }

        handleSuccess ? handleSuccess() : hideModal();
        handleRefresh?.();
        retrieveCustomerDevices?.();
        setSubmitProgress(false);

        if (isCommercialRole || isRegisterDevice) {
          handleChangeTicketLocation(res.id);
        }
      })
      .catch((err) => {
        setLoading(false);
        setSubmitProgress(false);
        console.log("Fail", err);
        // `err` is the rejection object from protectedFetch, not an Error -
        // concatenating it into a string would render "[object Object]".
        setFieldErrors(err?.errors || {});
        showToast(err?.message || "Failed to create ticket", "error");
      });
  }, [
    reason,
    ticketType,
    typeOfService,
    systemError,
    otherIssue,
    userDevice,
    selectedbrandID,
    selectedBrandIdForWarranty,
    problemFiles,
    customerId,
    dateOfReturn,
    dorAttachments,
    isCommercialRole,
    ticketAddress,
    protectedFetch,
    showToast,
    handleSuccess,
    hideModal,
    handleRefresh,
    retrieveCustomerDevices,
    isRegisterDevice,
    handleChangeTicketLocation,
  ]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setFieldErrors({});

      // react-datepicker renders a text input, so the browser's own required
      // check isn't reliable here.
      if (!dateOfReturn) {
        showToast("Please select the Date of Return", "warning");
        return;
      }

      if (!isCommercialRole) {
        setAddressMode("confirm");
        setShowAddressConfirm(true);
        return;
      }

      submitTicket();
    },
    [dateOfReturn, isCommercialRole, showToast, submitTicket],
  );

  const handleAddressEdit = useCallback(() => setAddressMode("edit"), []);
  const handleAddressBack = useCallback(() => setAddressMode("confirm"), []);
  const handleAddressClose = useCallback(
    () => setShowAddressConfirm(false),
    [],
  );
  const handleAddressConfirm = useCallback(
    () => submitTicket(),
    [submitTicket],
  );
  // Save only applies the correction and returns to the read-only confirm
  // view - the ticket is created only when the user explicitly clicks
  // Confirm afterwards, never as a side effect of editing.
  const handleAddressSaveEdit = useCallback((newAddress) => {
    setTicketAddress(newAddress);
    setAddressMode("confirm");
  }, []);

  // --- Derived ---
  const ticketTypes = useMemo(() => {
    const types = [
      {
        value: "warranty",
        label: "Warranty or Service Claims (within 1 - 2 years)",
      },
    ];
    if (selectedBrand !== "steelseries" || !ticketReasonData.hasCategories) {
      types.push({
        value: "out_of_warranty",
        label: "Service (Out of Warranty)",
      });
    }
    return types;
  }, [selectedBrand]);

  return (
    <Modal
      isOpen={showModal}
      contentLabel="Set warranty details"
      className="set-warranty-modal"
      style={{ overlay: { zIndex: 1001 } }}
    >
      <div
        className="modal show d-block"
        tabIndex="-1"
        role="dialog"
        style={{ background: "rgba(0,0,0,0.5)", zIndex: 1001 }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          style={{ maxWidth: 800 }}
        >
          <div className="modal-content">
            <div
              className="modal-header flex-column align-items-center border-bottom-2 pb-2"
              style={{ position: "relative" }}
            >
              {/* <img
                className="NavbarLogo mb-2"
                src={logo}
                alt="logo"
                style={{ height: 50, width: "auto" }}
              /> */}
              <h2 className="pageHeader text-center w-100 mb-2">
                Warranty Request
              </h2>
              <button
                type="button"
                className="btn-close position-absolute end-0 top-0 m-3"
                aria-label="Close"
                onClick={hideModal}
              ></button>
            </div>
            <div
              className="modal-body"
              style={{ height: "80vh", overflowY: "auto" }}
            >
              <div className="page add-new-warranty-page">
                <div className="pageContent">
                  <form onSubmit={handleSubmit}>
                    <TicketTypeSelect
                      value={ticketType}
                      onChange={(value) =>
                        handleInputChange({
                          target: { id: "ticketType", value },
                        })
                      }
                      options={ticketTypes}
                    />
                    <div className="add-new-warranty-special-input-header">
                      Please select and provide reason of return below:
                    </div>

                    <TicketReasonsCheckboxes
                      hasCategories={ticketReasonData.hasCategories}
                      ticketReasonsGrouped={ticketReasonData.grouped}
                      ticketReasons={ticketReasonData.reasons}
                      ticketReasonsLoading={ticketReasonsLoading}
                      onCheckboxChange={handleCheckboxChange}
                      onOtherIssueChange={handleOtherIssueReason}
                      reasonOther={reasonOther}
                    />

                    <DateOfReturnField
                      value={dateOfReturn}
                      onChange={(val) => {
                        setDateOfReturn(val);
                        setFieldErrors((prev) => ({
                          ...prev,
                          date_of_return: undefined,
                        }));
                      }}
                      error={fieldErrors[dateOfReturnFns.DATE_OF_RETURN_FIELD]}
                    />

                    <ProofOfReturnField
                      files={dorAttachments}
                      onFileChange={handleDorAttachmentChange}
                      onRemove={handleRemoveDorAttachment}
                      error={fieldErrors[dateOfReturnFns.DOR_ATTACHMENT_FIELD]}
                    />

                    <IssueDetailsFields onChange={handleInputChange} />

                    <AttachmentsUploader
                      files={problemFiles}
                      onFileUpload={handleFileUpload}
                      onRemoveFile={handleRemoveFile}
                    />

                    {submitProgress || loading ? (
                      <LoadingPage small={true} />
                    ) : (
                      <button
                        disabled={submitProgress || loading}
                        className="k-btn k-btn-primary-full"
                        type="submit"
                      >
                        Submit
                      </button>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <AddressConfirmModal
        isOpen={showAddressConfirm}
        mode={addressMode}
        address={ticketAddress}
        loading={addressLoading}
        submitting={loading}
        protectedFetch={protectedFetch}
        userInfo={userInfo}
        onConfirm={handleAddressConfirm}
        onEdit={handleAddressEdit}
        onSaveEdit={handleAddressSaveEdit}
        onBackToConfirm={handleAddressBack}
        onClose={handleAddressClose}
      />
    </Modal>
  );
};

export default SetWarrantyDetails;
